export type EmbedIconCorner = 'bottomRight' | 'bottomLeft' | 'topRight' | 'topLeft';

export type IconOffset = {
  top?: number;
  right?: number;
  bottom?: number;
  left?: number;
};

export interface AssistantConfigWindow extends Window {
  assistantConfig: Option;
}

export interface Option {
  name?: string;
  target?: string;
  accessCode?: string;
  assetUID?: string;
  cst?: string;
  context?: string;
  flags?: string;
  /**
   * Viewport corner for the chat launcher. Defaults to bottomRight when omitted or invalid.
   */
  iconCorner?: EmbedIconCorner;
  /**
   * Extra px inset on anchor edges; added to corner defaults (base is 0 on small viewports).
   */
  iconOffset?: IconOffset;
  /**
   * Optional z-index for the launcher container. Defaults to 9999 when omitted.
   */
  launcherZIndex?: number;
  /**
   * Optional absolute URL for the launcher icon shown before the chat is first
   * opened. Once opened, the tenant's configured embed logo takes precedence.
   */
  launcherIcon?: string;
  /**
   * Optional CSS colour for the default launcher button shown before the chat
   * is first opened. Once opened, the tenant palette takes precedence.
   */
  launcherColour?: string;
}

export interface WindowMessage {
  data: string;
}

export interface WindowMessageWithPayload {
  data: { type: string; payload: unknown };
}

/**
 * The maintenance contract between the chat iframe and its host page (NS-2464).
 *
 * Host pages — ours and customers' — already receive `maintenance`, so that
 * message is extended rather than replaced: existing listeners keep working and
 * simply ignore the payload. The end of a window is a **separate type** for the
 * same reason. Reusing `maintenance` with a status field would make every
 * existing `type === 'maintenance'` listener read the end of a window as the
 * start of one, breaking them in the act of fixing them.
 */
export const MAINTENANCE_MESSAGE = 'maintenance';

/** Sent once when a window ends. New type, so pre-existing listeners ignore it. */
export const MAINTENANCE_ENDED_MESSAGE = 'maintenance-ended';

export interface MaintenanceMessagePayload {
  /**
   * Whether reads are still being served. True for the normal read-only window,
   * in which the widget stays usable; false when nothing is being served and the
   * widget replaces itself.
   */
  readsAvailable: boolean;
  /** Operator classification of the window, when a refused write has revealed it. */
  windowType?: string;
  /** ISO-8601 expected end of the window, when known. */
  estimatedEnd?: string;
}

export interface MaintenanceMessage {
  type: typeof MAINTENANCE_MESSAGE;
  payload: MaintenanceMessagePayload;
}

export interface MaintenanceEndedMessage {
  type: typeof MAINTENANCE_ENDED_MESSAGE;
}
